import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="table-of-contents"
export default class extends Controller {
  static targets = ["link", "section"];
  static values = {
    offset: { type: Number, default: 100 }, // Offset for scroll position
  };

  connect() {
    this.activeLink = null;
    this.observer = null;
    this.scrollContainer = this.findScrollContainer();
    this.setupIntersectionObserver();
    this.updateActiveLink();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  findScrollContainer() {
    // Find the scrollable parent container
    let element = this.element;
    while (element && element !== document.body) {
      const style = getComputedStyle(element);
      const isScrollable = style.overflow === 'auto' || style.overflow === 'scroll' ||
                          style.overflowY === 'auto' || style.overflowY === 'scroll';
      if (isScrollable && element.scrollHeight > element.clientHeight) {
        return element;
      }
      element = element.parentElement;
    }
    return null; // Fall back to window scrolling
  }

  setupIntersectionObserver() {
    const options = {
      root: this.scrollContainer,
      rootMargin: `-${this.offsetValue}px 0px -50% 0px`,
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("id");
          this.setActiveLink(id);
        }
      });
    }, options);

    // Observe all sections
    this.sectionTargets.forEach((section) => {
      this.observer.observe(section);
    });
  }

  setActiveLink(id) {
    // Remove active state from all links
    this.linkTargets.forEach((link) => {
      link.classList.remove(
        "text-stone-900",
        "font-semibold",
        "border-l-2",
        "border-stone-900"
      );
      link.classList.add("text-stone-600", "border-l-2", "border-transparent");
    });

    // Add active state to the current link
    const activeLink = this.linkTargets.find(
      (link) => link.getAttribute("href") === `#${id}`
    );

    if (activeLink) {
      activeLink.classList.remove("text-stone-600", "border-transparent");
      activeLink.classList.add(
        "text-stone-900",
        "font-semibold",
        "border-stone-900"
      );
      this.activeLink = activeLink;
    }
  }

  scrollToSection(event) {
    event.preventDefault();
    const targetId = event.currentTarget.getAttribute("href").substring(1);
    const targetSection = document.getElementById(targetId);

    if (targetSection) {
      if (this.scrollContainer) {
        // Scroll within the container
        const containerRect = this.scrollContainer.getBoundingClientRect();
        const sectionRect = targetSection.getBoundingClientRect();
        const offsetPosition =
          this.scrollContainer.scrollTop + sectionRect.top - containerRect.top - this.offsetValue;

        this.scrollContainer.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      } else {
        // Fall back to window scrolling
        const elementPosition = targetSection.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - this.offsetValue;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }

      // Update active link immediately
      this.setActiveLink(targetId);
    }
  }

  updateActiveLink() {
    // Find the first visible section
    const scrollPosition = this.scrollContainer
      ? this.scrollContainer.scrollTop + this.offsetValue
      : window.scrollY + this.offsetValue;

    for (let i = this.sectionTargets.length - 1; i >= 0; i--) {
      const section = this.sectionTargets[i];
      if (section.offsetTop <= scrollPosition) {
        this.setActiveLink(section.getAttribute("id"));
        break;
      }
    }
  }
}
